/**
 * VerificationPipeline — reusable applicant lifecycle visualization.
 *
 * Renders the official 6-stage workflow (Application Submitted → Screening &
 * Reports → Verification & Validation → Additional Information Needed →
 * Decision-Ready Review) followed by the "Complete" outcome state.
 *
 * This is the same visual that runs on the public landing page Visibility
 * mock, lifted into a real component so logged-in dashboards stay 1:1 with
 * marketing materials. Pass a `stages` array of `{ key, count }` (or omit
 * `count` to render the empty skeleton).
 */
import {
  PIPELINE_STAGES,
  PIPELINE_DOT,
  PIPELINE_BAR,
  PIPELINE_FILL,
} from "@/lib/verifyHub";

export default function VerificationPipeline({
  stages,
  activeStageKey,
  onStageClick,
  title = "Verification Pipeline",
  subtitle,
  testid = "verification-pipeline",
}) {
  const countByKey = Object.fromEntries((stages || []).map((s) => [s.key, s.count]));
  // Normalize bar widths by the largest stage count so high-volume tenants
  // keep visual differentiation between stages (790 vs 24 vs 10 stays
  // legible) and small tenants still see proportional bars.
  const maxCount = Math.max(1, ...PIPELINE_STAGES.map((s) => countByKey[s.key] ?? 0));
  const interactive = typeof onStageClick === "function";

  return (
    <section
      className="bg-white rounded-xl ring-1 ring-slate-200 shadow-sm overflow-hidden"
      data-testid={testid}
    >
      <header className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">
            Pipeline
          </div>
          <div className="font-serif text-base text-slate-900 mt-0.5">{title}</div>
        </div>
        {subtitle && (
          <div className="text-[11px] text-slate-500 font-mono tracking-wider">{subtitle}</div>
        )}
      </header>

      <div className="p-5 space-y-2.5">
        {PIPELINE_STAGES.map((s) => {
          const count = countByKey[s.key] ?? 0;
          const isHot = activeStageKey && s.key === activeStageKey;
          const Icon = s.icon;
          const Tag = interactive ? "button" : "div";
          return (
            <Tag
              key={s.key}
              type={interactive ? "button" : undefined}
              onClick={interactive ? () => onStageClick(s.key) : undefined}
              className={`w-full text-left flex items-center gap-3 rounded-md px-2 py-1.5 transition-all duration-300
                ${isHot ? "bg-blue-50/70 ring-1 ring-blue-200/70" : ""}
                ${interactive ? "hover:bg-slate-50 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" : ""}`}
              data-testid={`pipeline-stage-${s.key}`}
              data-active={isHot ? "true" : "false"}
            >
              <div
                className={`h-2.5 w-2.5 rounded-full ${PIPELINE_DOT[s.tone]} shrink-0 ${
                  isHot ? "scale-125" : ""
                } transition-transform duration-300`}
              />
              <Icon className="h-4 w-4 text-slate-500 shrink-0" strokeWidth={1.7} />
              <div className="flex-1 text-sm text-slate-700 truncate">{s.label}</div>
              <div className={`h-1.5 rounded-full ${PIPELINE_BAR[s.tone]} w-28 sm:w-40 overflow-hidden`}>
                <div
                  className={`h-full ${PIPELINE_FILL[s.tone]} transition-all duration-500`}
                  style={{ width: `${count === 0 ? 0 : Math.max(8, Math.round((count / maxCount) * 100))}%` }}
                />
              </div>
              <div
                className="text-sm font-semibold tabular-nums text-slate-700 w-8 text-right"
                data-testid={`pipeline-stage-${s.key}-count`}
              >
                {count}
              </div>
            </Tag>
          );
        })}
      </div>
    </section>
  );
}
